import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@mantine/core';
import { z } from 'zod';

import { Button } from '../buttons/Button';
import { ErrorMessage } from '../error-message/ErrorMessage';

import { LoginWidgetProps } from './LoginWidget';

import styles from './loginForm.module.scss';

export type LoginFormProps = {
  emailErrorMsg?: string;
  introductionText?: string;
  isSso: boolean;
  loginError?: string;
  passwordErrorMsg?: string;
  onClickForgotPwButton: LoginWidgetProps['onResetPassword'];
  onEnterValidEmail?: LoginWidgetProps['onEnterValidEmail'];
  onSubmit: LoginWidgetProps['onLoginSubmit'];
};

export function LoginForm({
  emailErrorMsg,
  introductionText,
  isSso,
  loginError,
  passwordErrorMsg,
  onClickForgotPwButton,
  onEnterValidEmail,
  onSubmit,
}: LoginFormProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const emailInvalid = !z.string().email().safeParse(email.trim()).success;

  useEffect(() => {
    if (onEnterValidEmail == null || emailInvalid) {
      return;
    }
    onEnterValidEmail(email.trim());
  }, [email, onEnterValidEmail, emailInvalid]);

  const submit: React.FormEventHandler = (evt) => {
    evt.preventDefault();
    onSubmit(email.trim(), password);
  };

  const submitDisabled = emailInvalid || (!isSso && !password);

  return (
    <form className={styles['root']} onSubmit={submit}>
      <span>{introductionText ?? t('app.loginWidget.loginWelcome')}</span>
      <div className={styles['root__body']}>
        <TextInput
          autoComplete="email"
          error={emailErrorMsg}
          label={t('app.loginWidget.email')}
          onChange={(event) => setEmail(event.currentTarget.value)}
          placeholder={t('app.loginWidget.email') ?? undefined}
          tabIndex={1}
          value={email}
        />
        {!isSso && (
          <TextInput
            autoComplete="current-password"
            error={passwordErrorMsg}
            label={t('app.loginWidget.password')}
            onChange={(event) => setPassword(event.currentTarget.value)}
            placeholder={t('app.loginWidget.password') ?? undefined}
            tabIndex={2}
            type="password"
            value={password}
          />
        )}
        <Button
          onClick={() => onClickForgotPwButton(email.trim())}
          tabIndex={4}
          variant="text"
        >
          {t('app.loginWidget.forgotPassword')}
        </Button>
      </div>
      <div className={styles['root__footer']}>
        {loginError && (
          <div className={styles['error-message']}>
            <ErrorMessage>{loginError}</ErrorMessage>
          </div>
        )}
        <Button disabled={submitDisabled} enlarge tabIndex={3} type="submit">
          {isSso ? t('app.loginWidget.next') : t('app.loginWidget.login')}
        </Button>
      </div>
    </form>
  );
}
